import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import ShoppingCart from '../../components/ui/ShoppingCart';
import ContactHub from '../../components/ui/ContactHub';
import FilterSidebar from './components/FilterSidebar';
import SearchAndSort from './components/SearchAndSort';
import ProductGrid from './components/ProductGrid';
import CartSidebar from './components/CartSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const ECommerceStore = () => {
  const [products] = useState([
    {
      id: 1,
      name: "Professional Carpet Cleaning Solution",
      category: "cleaning-supplies",
      brand: "clean360",
      price: 29.99,
      originalPrice: 39.99,
      rating: 4.8,
      reviewCount: 124,
      stock: 15,
      image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop",
      isNew: true,
      variants: [
        { id: 1, name: "32oz", price: 29.99 },
        { id: 2, name: "64oz", price: 49.99 }
      ]
    },
    {
      id: 2,
      name: "Heavy-Duty Floor Scrubber",
      category: "equipment",
      brand: "powerclean",
      price: 199.99,
      rating: 4.6,
      reviewCount: 89,
      stock: 8,
      image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400&h=300&fit=crop",
      variants: [
        { id: 1, name: "Corded", price: 199.99 },
        { id: 2, name: "Cordless", price: 249.99 }
      ]
    },
    {
      id: 3,
      name: "Eco-Friendly All-Purpose Cleaner",
      category: "cleaning-supplies",
      brand: "ecogreen",
      price: 12.99,
      rating: 4.7,
      reviewCount: 203,
      stock: 32,
      image: "https://images.unsplash.com/photo-1563453392212-326f5e854473?w=400&h=300&fit=crop",
      isNew: false
    },
    {
      id: 4,
      name: "Microfiber Cleaning Cloths Set",
      category: "accessories",
      brand: "clean360",
      price: 19.99,
      originalPrice: 24.99,
      rating: 4.9,
      reviewCount: 156,
      stock: 45,
      image: "https://images.unsplash.com/photo-1584464491033-06628f3a6b7b?w=400&h=300&fit=crop"
    },
    {
      id: 5,
      name: "Industrial Vacuum Cleaner",
      category: "equipment",
      brand: "industrial-grade",
      price: 399.99,
      rating: 4.5,
      reviewCount: 67,
      stock: 3,
      image: "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=400&h=300&fit=crop"
    },
    {
      id: 6,
      name: "Glass & Mirror Cleaner",
      category: "cleaning-supplies",
      brand: "naturefresh",
      price: 8.99,
      rating: 4.4,
      reviewCount: 91,
      stock: 28,
      image: "https://images.unsplash.com/photo-1527515637462-cff94eecc1ac?w=400&h=300&fit=crop"
    },
    {
      id: 7,
      name: "Pressure Washer Attachment Kit",
      category: "tools",
      brand: "powerclean",
      price: 79.99,
      originalPrice: 99.99,
      rating: 4.6,
      reviewCount: 45,
      stock: 12,
      image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400&h=300&fit=crop"
    },
    {
      id: 8,
      name: "Disinfectant Spray Bundle",
      category: "chemicals",
      brand: "clean360",
      price: 34.99,
      rating: 4.8,
      reviewCount: 178,
      stock: 22,
      image: "https://images.unsplash.com/photo-1584464491033-06628f3a6b7b?w=400&h=300&fit=crop",
      isNew: true
    }
  ]);

  const [filteredProducts, setFilteredProducts] = useState(products);
  const [filters, setFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('relevance');
  const [viewMode, setViewMode] = useState('grid');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const itemsPerPage = 12;
  const totalPages = Math.ceil(filteredProducts?.length / itemsPerPage);
  const paginatedProducts = filteredProducts?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Filter and search products
  useEffect(() => {
    let filtered = [...products];

    // Apply search
    if (searchQuery) {
      filtered = filtered?.filter(product =>
        product?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        product?.category?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        product?.brand?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Apply category filter
    if (filters?.categories?.length) {
      filtered = filtered?.filter(product =>
        filters?.categories?.includes(product?.category)
      );
    }

    // Apply brand filter
    if (filters?.brands?.length) {
      filtered = filtered?.filter(product =>
        filters?.brands?.includes(product?.brand)
      );
    }

    // Apply price range filter
    if (filters?.priceRange) {
      filtered = filtered?.filter(product =>
        product?.price >= filters?.priceRange?.[0] && product?.price <= filters?.priceRange?.[1]
      );
    }

    // Apply rating filter
    if (filters?.minRating) {
      filtered = filtered?.filter(product =>
        product?.rating >= filters?.minRating
      );
    }

    // Apply availability filters
    if (filters?.inStock) {
      filtered = filtered?.filter(product => product?.stock > 0);
    }

    if (filters?.onSale) {
      filtered = filtered?.filter(product => product?.originalPrice && product?.originalPrice > product?.price);
    }

    if (filters?.newArrivals) {
      filtered = filtered?.filter(product => product?.isNew);
    }

    // Apply sorting
    switch (sortBy) {
      case 'price-low':
        filtered?.sort((a, b) => a?.price - b?.price);
        break;
      case 'price-high':
        filtered?.sort((a, b) => b?.price - a?.price);
        break;
      case 'rating':
        filtered?.sort((a, b) => b?.rating - a?.rating);
        break;
      case 'newest':
        filtered?.sort((a, b) => (b?.isNew ? 1 : 0) - (a?.isNew ? 1 : 0));
        break;
      case 'name':
        filtered?.sort((a, b) => a?.name?.localeCompare(b?.name));
        break;
      case 'popular':
        filtered?.sort((a, b) => b?.reviewCount - a?.reviewCount);
        break;
      default:
        // relevance - keep original order
        break;
    }

    setFilteredProducts(filtered);
    setCurrentPage(1);
  }, [products, filters, searchQuery, sortBy]);

  const handleAddToCart = (product) => {
    const existingItem = cartItems?.find(item => 
      item?.id === product?.id && 
      item?.variant?.id === product?.variant?.id
    );

    if (existingItem) {
      setCartItems(items =>
        items?.map(item =>
          item?.id === product?.id && item?.variant?.id === product?.variant?.id
            ? { ...item, quantity: item?.quantity + product?.quantity }
            : item
        )
      );
    } else {
      setCartItems(items => [...items, product]);
    }

    // Show success message or animation
    console.log('Added to cart:', product);
  };

  const handleAddToWishlist = (productId) => {
    setWishlistItems(items =>
      items?.includes(productId)
        ? items?.filter(id => id !== productId)
        : [...items, productId]
    );
  };

  const handleUpdateCartQuantity = (productId, variantId, newQuantity) => {
    if (newQuantity === 0) {
      handleRemoveFromCart(productId, variantId);
      return;
    }

    setCartItems(items =>
      items?.map(item =>
        item?.id === productId && item?.variant?.id === variantId
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };

  const handleRemoveFromCart = (productId, variantId) => {
    setCartItems(items =>
      items?.filter(item =>
        !(item?.id === productId && item?.variant?.id === variantId)
      )
    );
  };

  const handleCheckout = () => {
    console.log('Proceeding to checkout with items:', cartItems);
    setShowCart(false);
    // Navigate to checkout page
  };

  const handleToggleFilters = () => {
    setShowMobileFilters(!showMobileFilters);
  };

  return (
    <>
      <Helmet>
        <title>E-Commerce Store - Clean360 Platform</title>
        <meta name="description" content="Shop professional cleaning supplies, equipment, and accessories. Browse our extensive catalog with smart recommendations and bulk pricing options." />
        <meta name="keywords" content="cleaning supplies, equipment, chemicals, tools, accessories, professional cleaning, bulk pricing" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          {/* Hero Section */}
          <div className="bg-gradient-to-r from-primary to-primary/80 text-white py-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center">
                <h1 className="text-3xl md:text-4xl font-bold mb-4">
                  Professional Cleaning Supplies
                </h1>
                <p className="text-lg md:text-xl text-white/90 mb-6 max-w-2xl mx-auto">
                  Discover our comprehensive range of cleaning products, equipment, and accessories. 
                  Quality supplies for professionals and homeowners alike.
                </p>
                <div className="flex flex-wrap justify-center gap-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <Icon name="Truck" size={16} />
                    <span>Free shipping over $75</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Shield" size={16} />
                    <span>Quality guaranteed</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Clock" size={16} />
                    <span>Fast delivery</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex gap-8">
              {/* Filter Sidebar */}
              <FilterSidebar
                filters={filters}
                onFiltersChange={setFilters}
                isOpen={showMobileFilters}
                onClose={() => setShowMobileFilters(false)}
              />

              {/* Main Content Area */}
              <div className="flex-1 min-w-0">
                {/* Search and Sort */}
                <SearchAndSort
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  sortBy={sortBy}
                  onSortChange={setSortBy}
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  resultsCount={filteredProducts?.length}
                  onToggleFilters={handleToggleFilters}
                  showMobileFilters={showMobileFilters}
                />

                {/* Product Grid */}
                <div className="mt-6">
                  <ProductGrid
                    products={paginatedProducts}
                    loading={loading}
                    onAddToCart={handleAddToCart}
                    onAddToWishlist={handleAddToWishlist}
                    wishlistItems={wishlistItems}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Features Section */}
          <div className="bg-muted py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-4">
                  Why Choose Our Store?
                </h2>
                <p className="text-text-secondary max-w-2xl mx-auto">
                  We provide the best shopping experience with quality products and exceptional service.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Truck" size={24} color="white" />
                  </div>
                  <h3 className="font-semibold text-text-primary mb-2">Free Shipping</h3>
                  <p className="text-sm text-text-secondary">
                    Free delivery on orders over $75. Fast and reliable shipping nationwide.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-success rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Shield" size={24} color="white" />
                  </div>
                  <h3 className="font-semibold text-text-primary mb-2">Quality Guarantee</h3>
                  <p className="text-sm text-text-secondary">
                    All products are tested and guaranteed for professional-grade performance.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-warning rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Headphones" size={24} color="white" />
                  </div>
                  <h3 className="font-semibold text-text-primary mb-2">Expert Support</h3>
                  <p className="text-sm text-text-secondary">
                    Get professional advice from our cleaning experts and technical support team.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Percent" size={24} color="white" />
                  </div>
                  <h3 className="font-semibold text-text-primary mb-2">Bulk Discounts</h3>
                  <p className="text-sm text-text-secondary">
                    Save more with quantity discounts and special pricing for businesses.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Cart Sidebar */}
        <CartSidebar
          isOpen={showCart}
          onClose={() => setShowCart(false)}
          cartItems={cartItems}
          onUpdateQuantity={handleUpdateCartQuantity}
          onRemoveItem={handleRemoveFromCart}
          onCheckout={handleCheckout}
        />

        {/* Floating Action Buttons */}
        <div className="fixed bottom-6 right-6 flex flex-col space-y-3 z-30">
          {/* Cart Button */}
          <Button
            variant="default"
            size="lg"
            onClick={() => setShowCart(true)}
            className="rounded-full shadow-elevation-4 hover:shadow-elevation-5 transition-all duration-300 relative"
          >
            <Icon name="ShoppingCart" size={24} />
            {cartItems?.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-error text-white text-xs font-medium rounded-full w-6 h-6 flex items-center justify-center">
                {cartItems?.reduce((sum, item) => sum + item?.quantity, 0)}
              </span>
            )}
          </Button>

          {/* Wishlist Button */}
          <Button
            variant="outline"
            size="lg"
            className="rounded-full shadow-elevation-3 hover:shadow-elevation-4 transition-all duration-300 relative bg-white"
          >
            <Icon name="Heart" size={24} />
            {wishlistItems?.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-error text-white text-xs font-medium rounded-full w-6 h-6 flex items-center justify-center">
                {wishlistItems?.length}
              </span>
            )}
          </Button>
        </div>

        <ShoppingCart />
        <ContactHub />
      </div>
    </>
  );
};

export default ECommerceStore;